import type { QueryClient } from '@tanstack/react-query';
import type { ConversationSearchScope } from '../../../types/conversation-search-refresh.types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Recovers pre-edit query ownership from existing mutation contexts. Bulk job
 * acceptance can precede its socket event, so successful contexts remain useful
 * during the existing 30-second recovery window. No model revisions are inferred.
 * @param client - Current account's mutation and query owner.
 * @param id - Accepted conversation identity.
 * @param tenantId - Authorized resource tenant.
 * @returns Still-owned source/destination instances retained before local edits.
 */
export const recentConversationMutationScope = (
  client: QueryClient,
  id: number,
  tenantId: string
): ConversationSearchScope => {
  const queries = client
    .getQueryCache()
    .findAll({ queryKey: ['crm/documents/funnel-conversations'] });
  const result = new Set<(typeof queries)[number]>();
  client
    .getMutationCache()
    .findAll({ mutationKey: ['crm/funnel-conversations/update'] })
    .forEach(({ state }) => {
      if (state.status === 'error' || state.submittedAt < Date.now() - 30_000) {
        return;
      }
      const context: unknown = state.context;
      const snapshot: unknown =
        isRecord(context) && context.snapshots instanceof Map
          ? context.snapshots.get(id)
          : context;
      if (
        !isRecord(snapshot) ||
        !isRecord(snapshot.params) ||
        snapshot.params.id !== id ||
        !(snapshot.searchScope instanceof Set)
      ) {
        return;
      }
      const previous =
        snapshot.previousConversation ?? snapshot.previousConversationDocument;
      if (!isRecord(previous) || previous.tenantId !== tenantId) return;
      const scope = snapshot.searchScope;
      queries.forEach((query) => {
        if (scope.has(query)) result.add(query);
      });
    });
  return result;
};
